// @ts-ignore
import { hasProp, assert } from "@utils/assertion";
// @ts-ignore
import { isArray } from "@utils/arrayUtils";
// @ts-ignore
import { parseJsonWithNumbersAsBigint, renderJsonWithNumbersAsBigint, type ParserResult } from "@utils/json";
// @ts-ignore
import { toBase64 } from "@utils/blobUtils";
// @ts-ignore
import { DateTime } from "luxon";
// @ts-ignore
import { type ReadonlyUint8Array } from "@utils/arrayUtils";
import type { FormFilterName, FormFilterOption, FormFilterPriority, FormFilterType, FormFilter } from "../schema";
import { serialize_FormFilterName_internal, deserialize_FormFilterName_internal } from "./FormFilterName";
import { serialize_FormFilterOption_internal, deserialize_FormFilterOption_internal } from "./FormFilterOption";
import { serialize_FormFilterPriority_internal, deserialize_FormFilterPriority_internal } from "./FormFilterPriority";
import { serialize_FormFilterType_internal, deserialize_FormFilterType_internal } from "./FormFilterType";

export function serialize_FormFilter_internal(value: FormFilter): unknown {
  return ((value: { activeValues: string[]; identifier: FormFilterName; label: string; name: string; options: FormFilterOption[]; priority: FormFilterPriority; type: FormFilterType }): unknown => {
    return {
      activeValues: ((value: string[]): unknown => {
        return value.map((value: string): unknown => {
          return value;
        });
      })(value["activeValues"]),
      identifier: ((value: FormFilterName): unknown => {
        return serialize_FormFilterName_internal(value);
      })(value["identifier"]),
      label: ((value: string): unknown => {
        return value;
      })(value["label"]),
      name: ((value: string): unknown => {
        return value;
      })(value["name"]),
      options: ((value: FormFilterOption[]): unknown => {
        return value.map((value: FormFilterOption): unknown => {
          return serialize_FormFilterOption_internal(value);
        });
      })(value["options"]),
      priority: ((value: FormFilterPriority): unknown => {
        return serialize_FormFilterPriority_internal(value);
      })(value["priority"]),
      type: ((value: FormFilterType): unknown => {
        return serialize_FormFilterType_internal(value);
      })(value["type"]),
    };
  })(value);
}
export function serialize_FormFilter(obj: FormFilter): string {
  return renderJsonWithNumbersAsBigint(serialize_FormFilter_internal(obj));
}
export function deserialize_FormFilter_internal(value: unknown): ParserResult<FormFilter> {
  return ((
    value: unknown,
  ): ParserResult<{
    activeValues: string[];
    identifier: FormFilterName;
    label: string;
    name: string;
    options: FormFilterOption[];
    priority: FormFilterPriority;
    type: FormFilterType;
  }> => {
    if (typeof value !== "object") {
      return { success: false, reason: `[#/components/schemas/FormFilter]: '${value}' expected to be of type 'object'.` };
    }
    if (value === null) {
      return { success: false, reason: `[#/components/schemas/FormFilter]: '${value}' expected to not be null.` };
    }
    if (!hasProp(value, "activeValues")) {
      return { success: false, reason: `[#/components/schemas/FormFilter/activeValues]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'activeValues'.` };
    }
    const parsed_activeValues = ((): ParserResult<string[]> => {
      return ((value: unknown): ParserResult<string[]> => {
        if (!isArray(value)) {
          return { success: false, reason: `[#/components/schemas/FormFilter/activeValues]: '${value}' expected to be an array.` };
        }
        const parsed = value.map((value: unknown): ParserResult<string> => {
          if (typeof value !== "string") {
            return { success: false, reason: `[#/components/schemas/FormFilter/activeValues]: '${value}' expected to be of type string.` };
          }
          return { success: true, value: value };
        });
        if (parsed.find((value) => !value.success) !== undefined) {
          return {
            success: false,
            reason: `[#/components/schemas/FormFilter/activeValues]: Reason(s): '${parsed
              .filter((p) => !p.success)
              .map((p) => p.reason)
              .join(", ")}'.`,
          };
        }
        return {
          success: true,
          value: parsed.map((entry) => {
            assert(entry.success == true);
            return entry.value;
          }),
        };
      })(value["activeValues"]);
    })();
    if (!parsed_activeValues.success) {
      return { success: false, reason: parsed_activeValues.reason };
    }
    if (!hasProp(value, "identifier")) {
      return { success: false, reason: `[#/components/schemas/FormFilter/identifier]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'identifier'.` };
    }
    const parsed_identifier = ((): ParserResult<FormFilterName> => {
      return ((value: unknown): ParserResult<FormFilterName> => {
        return deserialize_FormFilterName_internal(value);
      })(value["identifier"]);
    })();
    if (!parsed_identifier.success) {
      return { success: false, reason: parsed_identifier.reason };
    }
    if (!hasProp(value, "label")) {
      return { success: false, reason: `[#/components/schemas/FormFilter/label]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'label'.` };
    }
    const parsed_label = ((): ParserResult<string> => {
      return ((value: unknown): ParserResult<string> => {
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/FormFilter/label]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["label"]);
    })();
    if (!parsed_label.success) {
      return { success: false, reason: parsed_label.reason };
    }
    if (!hasProp(value, "name")) {
      return { success: false, reason: `[#/components/schemas/FormFilter/name]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'name'.` };
    }
    const parsed_name = ((): ParserResult<string> => {
      return ((value: unknown): ParserResult<string> => {
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/FormFilter/name]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["name"]);
    })();
    if (!parsed_name.success) {
      return { success: false, reason: parsed_name.reason };
    }
    if (!hasProp(value, "options")) {
      return { success: false, reason: `[#/components/schemas/FormFilter/options]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'options'.` };
    }
    const parsed_options = ((): ParserResult<FormFilterOption[]> => {
      return ((value: unknown): ParserResult<FormFilterOption[]> => {
        if (!isArray(value)) {
          return { success: false, reason: `[#/components/schemas/FormFilter/options]: '${value}' expected to be an array.` };
        }
        const parsed = value.map((value: unknown): ParserResult<FormFilterOption> => {
          return deserialize_FormFilterOption_internal(value);
        });
        if (parsed.find((value) => !value.success) !== undefined) {
          return {
            success: false,
            reason: `[#/components/schemas/FormFilter/options]: Reason(s): '${parsed
              .filter((p) => !p.success)
              .map((p) => p.reason)
              .join(", ")}'.`,
          };
        }
        return {
          success: true,
          value: parsed.map((entry) => {
            assert(entry.success == true);
            return entry.value;
          }),
        };
      })(value["options"]);
    })();
    if (!parsed_options.success) {
      return { success: false, reason: parsed_options.reason };
    }
    if (!hasProp(value, "priority")) {
      return { success: false, reason: `[#/components/schemas/FormFilter/priority]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'priority'.` };
    }
    const parsed_priority = ((): ParserResult<FormFilterPriority> => {
      return ((value: unknown): ParserResult<FormFilterPriority> => {
        return deserialize_FormFilterPriority_internal(value);
      })(value["priority"]);
    })();
    if (!parsed_priority.success) {
      return { success: false, reason: parsed_priority.reason };
    }
    if (!hasProp(value, "type")) {
      return { success: false, reason: `[#/components/schemas/FormFilter/type]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'type'.` };
    }
    const parsed_type = ((): ParserResult<FormFilterType> => {
      return ((value: unknown): ParserResult<FormFilterType> => {
        return deserialize_FormFilterType_internal(value);
      })(value["type"]);
    })();
    if (!parsed_type.success) {
      return { success: false, reason: parsed_type.reason };
    }
    return {
      success: true,
      value: { activeValues: parsed_activeValues.value, identifier: parsed_identifier.value, label: parsed_label.value, name: parsed_name.value, options: parsed_options.value, priority: parsed_priority.value, type: parsed_type.value },
    };
  })(value);
}
export function deserialize_FormFilter(str: string): ParserResult<FormFilter> {
  return deserialize_FormFilter_internal(parseJsonWithNumbersAsBigint(str));
}
